import { FxAlert, FxSpinner } from '@/components/fx'
import { DialogShell } from './DialogShell'
import { CostSummary } from './LogSection'
import { COST_UNKNOWN, formatCount, formatUsd, purposeLabel, shortTime } from './format'
import { useLlmCalls } from './useLlmCalls'

/**
 * "Protokoll und Kosten", in full: one row per call, newest first.
 *
 * Every call is logged — a failed one too — because the cost of a failed
 * call is real and because the log is the only place from which one can tell
 * whether the deployment-wide switch is even on. Shares its query with
 * `LogSection`, so opening this costs nothing and the two can never
 * disagree.
 */
export function LlmCallLogDialog({
  templateId,
  open,
  onClose,
}: {
  templateId: string
  open: boolean
  onClose: () => void
}) {
  const calls = useLlmCalls(templateId, open)

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title="Protokoll und Kosten"
      description="Jeder Aufruf eines Sprachmodells für dieses Musterformular. Die Beträge sind Schätzungen zu Listenpreisen — ohne Rabatte, ohne Sammelverarbeitung."
      wide
    >
      {calls.isLoading ? (
        <FxSpinner label="Protokoll wird geladen …" />
      ) : calls.isError ? (
        <FxAlert tone="danger" title="Das Protokoll konnte nicht geladen werden" />
      ) : (
        <div className="flex flex-col gap-6">
          {calls.data?.summary && <CostSummary summary={calls.data.summary} />}

          <div>
            <h3 className="mb-2 mt-0 text-card-name text-text">Einzelne Aufrufe</h3>
            {/* The only element on this screen allowed to scroll sideways;
                seven columns do not fit a phone and squeezing them would
                make each unreadable. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-small">
                <thead>
                  <tr className="bg-fill text-left">
                    <th scope="col" className="whitespace-nowrap px-2 py-1 font-semibold">
                      Zeit
                    </th>
                    <th scope="col" className="px-2 py-1 font-semibold">
                      Vorgang
                    </th>
                    <th scope="col" className="px-2 py-1 font-semibold">
                      Modell
                    </th>
                    <th scope="col" className="whitespace-nowrap px-2 py-1 font-semibold">
                      Token ein/aus
                    </th>
                    <th scope="col" className="px-2 py-1 font-semibold">
                      Cache
                    </th>
                    <th scope="col" className="px-2 py-1 text-right font-semibold">
                      Kosten
                    </th>
                    <th scope="col" className="px-2 py-1 font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(calls.data?.calls ?? []).map((call) => {
                    const cache = call.cacheReadTokens + call.cacheWriteTokens
                    return (
                      <tr key={call.id} className="align-top">
                        <td className="whitespace-nowrap px-2 py-1">{shortTime(call.createdAt)}</td>
                        <td className="px-2 py-1">{purposeLabel(call.purpose)}</td>
                        <td className="px-2 py-1">{call.model || '–'}</td>
                        <td className="whitespace-nowrap px-2 py-1">
                          {formatCount(call.inputTokens)} / {formatCount(call.outputTokens)}
                        </td>
                        {/* A dash, not a zero: a row written before the
                            cache columns existed reports 0, which means
                            "not recorded" rather than "no cache". */}
                        <td className="px-2 py-1">{cache === 0 ? '–' : formatCount(cache)}</td>
                        <td className="px-2 py-1 text-right font-semibold">
                          {call.cost.priced ? formatUsd(call.cost.totalUsd) : COST_UNKNOWN}
                        </td>
                        <td
                          className={`px-2 py-1 ${call.ok ? 'text-success-text' : 'text-danger-text'}`}
                        >
                          {call.ok ? 'OK' : call.error || 'Fehler'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </DialogShell>
  )
}
